import {
  Box,
  Typography,
  Button,
  Switch,
  TextField,
  InputAdornment,
} from "@mui/material";
import Lottie from "react-lottie";
import { useContactCenterStyles } from "@components/virtual-receptionist/theming/useContactCenterStyles";
import { useEffect, useMemo, useRef, useState } from "react";
import { Search } from "@mui/icons-material";
import { lottieOptions } from "../../../jobs2/jobs-calendar-widget/components/TodayJobsPlaceholder";
import { CallFlowConfig } from "../types/update-company-config";
import { useSettingsMutation } from "../hooks/useSettingsMutation";
import { useAppSelector } from "../../../../redux/hooks/redux";
import { selectCurrentCompany } from "../../../../redux/features/user/selectors/userState.selectors";
import { useVoiceMailHistoryQuery } from "../voice-mail/hooks/useVoiceMailHistoryQuery";
import VoiceMailItem from "./VoiceMailItem";
import { useVirtualAssistant } from "@components/virtual-receptionist/context/VirtualAssistantContext";
import { useBulkResolveContacts } from "@components/virtual-receptionist/contacts/hooks/useBulkResolveContacts";
import {
  getDisplayName,
  getRoleLabel,
} from "@components/virtual-receptionist/contacts/types/bulk-resolver.types";

interface Props {
  settings: CallFlowConfig;
}

export default function VoiceMailSettings({ settings }: Props) {
  const styles = useContactCenterStyles();
  const { id: companyId } = useAppSelector(selectCurrentCompany);
  const { update } = useSettingsMutation();

  const { sessionNumber } = useVirtualAssistant();

  const [searchQuery, setSearchQuery] = useState("");
  const [voicemailEnabled, setVoicemailEnabled] = useState(
    settings.voicemailEnabled
  );

  const [editingVoice, setEditingVoice] = useState(false);
  const [status, setStatus] = useState<"idle" | "recording" | "finished">("idle");
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [volume, setVolume] = useState(1);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const recordingTimerRef = useRef<number | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const audioPlayerRef = useRef<HTMLAudioElement | null>(null);
  const animationRef = useRef<number | null>(null);

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } =
    useVoiceMailHistoryQuery({
      limit: 10,
      enabled: true,
      phoneNumber: sessionNumber,
    });

  useEffect(() => {
    setVoicemailEnabled(settings.voicemailEnabled);
  }, [settings.voicemailEnabled]);

  const voiceMails = useMemo(() => {
    return data?.pages.flatMap((page) => page.data) ?? [];
  }, [data]);

  const unresolvedPhones = useMemo(() => {
    const phones = voiceMails
      .filter((mail) => !mail.contact)
      .map((mail) => mail.from || mail.to)
      .filter(Boolean);

    return Array.from(new Set(phones));
  }, [voiceMails]);

  const { data: bulkResolvedMap = {} } =
    useBulkResolveContacts(unresolvedPhones);

  const filteredVoice = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return voiceMails;

    return voiceMails.filter((voice) => {
      const phoneNumber = voice.from || voice.to;
      const resolved = voice.contact || bulkResolvedMap?.[phoneNumber] || null;

      const from = voice.from?.toLowerCase() ?? "";
      const to = voice.to?.toLowerCase() ?? "";
      const recordingSid = voice.recordingSid?.toLowerCase() ?? "";
      const createdAt = voice.createdAt
        ? new Date(voice.createdAt).toLocaleDateString().toLowerCase()
        : "";

      const displayName = resolved
        ? getDisplayName(resolved)?.toLowerCase() ?? ""
        : "";

      const roleLabel = resolved
        ? getRoleLabel(resolved).toLowerCase()
        : "";

      return (
        from.includes(query) ||
        to.includes(query) ||
        recordingSid.includes(query) ||
        createdAt.includes(query) ||
        displayName.includes(query) ||
        roleLabel.includes(query)
      );
    });
  }, [voiceMails, searchQuery, bulkResolvedMap]);

  const handleToggleVoicemail = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const checked = event.target.checked;
    setVoicemailEnabled(checked);

    try {
      await update.mutateAsync({
        companyId,
        payload: { voicemailEnabled: checked },
      });
    } catch (error) {
      setVoicemailEnabled(!checked);
      console.error(error);
    }
  };

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
  };

  const updateProgress = () => {
    if (audioPlayerRef.current && !audioPlayerRef.current.paused) {
      setCurrentTime(audioPlayerRef.current.currentTime);
      animationRef.current = requestAnimationFrame(updateProgress);
    }
  };

  const startVisualizer = async (stream: MediaStream) => {
    const audioContext = new (window.AudioContext ||
      (window as any).webkitAudioContext)();

    if (audioContext.state === "suspended") {
      await audioContext.resume();
    }

    const source = audioContext.createMediaStreamSource(stream);
    const analyser = audioContext.createAnalyser();
    analyser.fftSize = 64;
    source.connect(analyser);

    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    const updateVolume = () => {
      if (mediaRecorderRef.current?.state !== "recording") {
        setVolume(1);
        return;
      }

      analyser.getByteFrequencyData(dataArray);
      const average = dataArray.reduce((a, b) => a + b) / bufferLength;
      const limitedScale = Math.min(1 + average / 100, 1.5);

      setVolume(limitedScale);
      requestAnimationFrame(updateVolume);
    };

    updateVolume();
  };

  const handleResetAudio = () => {
    if (audioPlayerRef.current) {
      audioPlayerRef.current.pause();
      audioPlayerRef.current.currentTime = 0;
      setCurrentTime(0);
      setIsPlaying(false);
    }
  };

  const handleStart = async () => {
    setEditingVoice(true);
    setStatus("recording");
    setRecordingTime(0);

    recordingTimerRef.current = window.setInterval(() => {
      setRecordingTime((prev) => prev + 1);
    }, 1000);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);

      mediaRecorderRef.current = recorder;
      chunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        setAudioUrl(URL.createObjectURL(blob));
      };

      recorder.start();
      startVisualizer(stream);
    } catch (err) {
      console.error("Error", err);
      if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
      setEditingVoice(false);
      setStatus("idle");
    }
  };

  const handleStop = () => {
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state !== "inactive"
    ) {
      mediaRecorderRef.current.stop();
    }

    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }

    setStatus("finished");
    setEditingVoice(false);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3, p: 2, flex: 1 }}>
      <Box sx={styles.topCard}>
        <Box>
          <Typography
            sx={{ fontWeight: 800, fontSize: "1rem", color: "var(--fx-surface-text, #0F172A)" }}
          >
            Voice mails
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
              fontWeight: 500,
              display: "block",
              lineHeight: 1.1,
              mt: 0.8,
            }}
          >
            Allow callers to leave a message when you're unavailable.
          </Typography>
        </Box>

        <Switch
          checked={voicemailEnabled}
          onChange={handleToggleVoicemail}
          disabled={update.isPending}
          sx={styles.accentSwitch}
        />
      </Box>

      <TextField
        fullWidth
        placeholder="Search voicemail..."
        variant="outlined"
        onChange={(e) => setSearchQuery(e.target.value)}
        sx={styles.searchField}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search sx={{ color: "var(--fx-surface-texttertiary, #94A3B8)" }} />
            </InputAdornment>
          ),
        }}
      />

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        px={1}
      >
        <Typography
          sx={{ fontWeight: 800, color: "var(--fx-surface-textsecondary, #64748B)", fontSize: ".95rem" }}
        >
          Recent Voice Mails
        </Typography>
        <Typography
          variant="caption"
          sx={{ color: "text.secondary", fontWeight: 600 }}
        >
          {filteredVoice.length} Result{filteredVoice.length !== 1 ? "s" : ""}
        </Typography>
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {isLoading ? (
          <Box
            sx={{
              ...styles.topCard,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              mb: 3,
            }}
          >
            <Typography
              sx={{
                fontSize: "0.85rem",
                color: "text.secondary",
                fontWeight: 500,
              }}
            >
              Loading voicemails...
            </Typography>
          </Box>
        ) : filteredVoice.length > 0 ? (
          <>
            {filteredVoice.map((mail) => (
              <VoiceMailItem
                key={mail.uid}
                mail={mail}
                bulkResolvedMap={bulkResolvedMap}
              />
            ))}

            {hasNextPage && (
              <Button
                variant="outlined"
                onClick={() => fetchNextPage()}
                disabled={isFetchingNextPage}
                sx={{
                  borderRadius: "12px",
                  textTransform: "none",
                  fontWeight: 700,
                }}
              >
                {isFetchingNextPage ? "Loading more..." : "Load more"}
              </Button>
            )}
          </>
        ) : (
          <Box
            sx={{
              ...styles.topCard,
              display: "flex",
              minHeight: 220,
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
              mb: 3,
            }}
          >
            <Lottie
              options={lottieOptions}
              height={50}
              width={50}
              isClickToPauseDisabled
            />
            <Typography
              sx={{
                fontSize: "0.75rem",
                color: "text.secondary",
                mt: 1,
                fontWeight: 500,
                lineHeight: 1.4,
              }}
            >
              {searchQuery
                ? `No voicemails found for "${searchQuery}"`
                : "No voicemails yet"}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}